import React, { useState, useEffect } from 'react';
import Button from '../../../components/Button';
import { Project } from '../../../services/projects';
import { ExtendedProjectMember, extendMember, agentToMember } from '../types/members';
import { getProjectAgents } from '../../../services/agents';

interface MembersTabProps {
  project: Project;
  canShareProject: boolean;
  onShareProject: () => void;
  currentUserEmail?: string;
}

const MembersTab: React.FC<MembersTabProps> = ({
  project,
  canShareProject,
  onShareProject,
  currentUserEmail
}) => {
  // State for AI agent filtering
  const [agentFilter, setAgentFilter] = useState('All');
  const [agentSearch, setAgentSearch] = useState('');
  const [showAgents, setShowAgents] = useState(true);

  // State for members and agents
  const [extendedMembers, setExtendedMembers] = useState<ExtendedProjectMember[]>([]);
  
  // Initialize members when project data is loaded
  useEffect(() => {
    if (project && project.members) {
      // Convert regular members to extended members
      const members = project.members.map(extendMember);
      setExtendedMembers(members);
      
      // Fetch AI agents if project ID is available
      if (project.id) {
        const fetchAgents = async () => {
          try {
            const agents = await getProjectAgents(project.id);
            // Convert agents to extended members using the helper function
            const agentMembers = agents.map(agentToMember);
            
            setExtendedMembers(prev => [...prev.filter(m => !m.isAI), ...agentMembers]);
          } catch (error) {
            console.error('Error fetching project agents:', error);
          }
        };
        
        fetchAgents();
      }
    }
  }, [project]);
  
  // Separate human members and AI agents
  const humanMembers = extendedMembers.filter(member => !member.isAI);
  const aiAgents = extendedMembers.filter(member => member.isAI);

  // Filter AI agents based on selected filter and search term
  const filteredAgents = aiAgents.filter(agent => {
    const matchesFilter = agentFilter === 'All' || agent.department === agentFilter;
    const matchesSearch = 
      (agent.name?.toLowerCase() || '').includes(agentSearch.toLowerCase()) || 
      (agent.email?.toLowerCase() || '').includes(agentSearch.toLowerCase()) ||
      (agent.description?.toLowerCase() || '').includes(agentSearch.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  // Sort agents alphabetically
  const sortedAgents = [...filteredAgents].sort((a, b) => 
    (a.name || a.email || '').localeCompare(b.name || b.email || '')
  );

  // Get unique departments for filter dropdown
  const departments = Array.from(
    new Set(aiAgents.map(agent => agent.department).filter(Boolean) as string[])
  );

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <h3 style={{ fontSize: '1.4rem', color: '#1e1e1e' }}>Project Team</h3>
        <div style={{ display: 'flex', gap: '1rem' }}>
          <Button 
            onClick={() => {}} // This will be implemented in a future update
            style={{ 
              backgroundColor: '#f0f0f0',
              color: '#1e1e1e',
              border: '1px solid #d9d9d9'
            }}
          >
            View Org Structure
          </Button>
          {canShareProject && (
            <Button 
              onClick={onShareProject}
              style={{ 
                backgroundColor: '#7FE7D9',
                color: '#1e1e1e'
              }}
            >
              Share Project
            </Button>
          )}
        </div>
      </div>
      
      {/* Human Members Section */}
      <div style={{ marginBottom: '2rem' }}>
        <h4 style={{ fontSize: '1.1rem', marginBottom: '1rem', color: '#1e1e1e' }}>Project Members</h4>
        
        {humanMembers.length > 0 ? (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ 
              width: '100%', 
              borderCollapse: 'collapse',
              backgroundColor: '#fff',
              borderRadius: '8px',
              overflow: 'hidden',
              boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
            }}>
              <thead>
                <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '1px solid #dee2e6' }}>
                  <th style={{ padding: '1rem', textAlign: 'left', fontWeight: 600 }}>Email</th>
                  <th style={{ padding: '1rem', textAlign: 'left', fontWeight: 600 }}>Role</th>
                  <th style={{ padding: '1rem', textAlign: 'left', fontWeight: 600 }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {humanMembers.map((member) => (
                  <tr key={member.email} style={{ borderBottom: '1px solid #dee2e6' }}>
                    <td style={{ padding: '1rem' }}>
                      {member.email}
                      {currentUserEmail === member.email && (
                        <span style={{ 
                          marginLeft: '0.5rem',
                          fontSize: '0.75rem',
                          color: '#666'
                        }}>
                          (You)
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '1rem' }}>
                      <span style={{ 
                        padding: '0.25rem 0.5rem', 
                        borderRadius: '4px',
                        fontSize: '0.85rem',
                        backgroundColor: 
                          member.role === 'owner' ? '#e6f7ff' : 
                          member.role === 'admin' ? '#f6ffed' : 
                          member.role === 'editor' ? '#fff7e6' :
                          '#f9f0ff',
                        color: 
                          member.role === 'owner' ? '#1890ff' : 
                          member.role === 'admin' ? '#52c41a' : 
                          member.role === 'editor' ? '#fa8c16' :
                          '#722ed1'
                      }}>
                        {member.role.charAt(0).toUpperCase() + member.role.slice(1)}
                      </span>
                    </td>
                    <td style={{ padding: '1rem' }}>
                      <span style={{ 
                        padding: '0.25rem 0.5rem', 
                        borderRadius: '4px',
                        fontSize: '0.85rem',
                        backgroundColor: member.status === 'active' ? '#f6ffed' : '#fff7e6',
                        color: member.status === 'active' ? '#52c41a' : '#fa8c16'
                      }}>
                        {member.status.charAt(0).toUpperCase() + member.status.slice(1)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '2rem', color: '#666', backgroundColor: '#f9f9f9', borderRadius: '8px' }}>
            <p>No members found. Click "Share Project" to invite team members.</p>
          </div>
        )}
      </div>
      
      {/* AI Agents Section */}
      <div>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          marginBottom: '1rem',
          cursor: 'pointer',
          padding: '0.5rem',
          borderRadius: '4px',
          backgroundColor: '#f8f9fa'
        }}
        onClick={() => setShowAgents(!showAgents)}
        >
          <h4 style={{ fontSize: '1.1rem', color: '#1e1e1e', margin: 0 }}>
            Assigned AI Agents {showAgents ? '▼' : '▶'}
          </h4>
          <span style={{ color: '#666', fontSize: '0.9rem' }}>
            {aiAgents.length} agents
          </span>
        </div>
        
        {showAgents && (
          <>
            {/* Filter and Search */}
            <div style={{ 
              display: 'flex', 
              gap: '1rem', 
              marginBottom: '1rem',
              flexWrap: 'wrap'
            }}>
              <div>
                <select
                  value={agentFilter}
                  onChange={(e) => setAgentFilter(e.target.value)}
                  style={{ 
                    padding: '0.5rem',
                    borderRadius: '4px',
                    border: '1px solid #d9d9d9'
                  }}
                >
                  <option value="All">All Departments</option>
                  {departments.map(dept => (
                    <option key={dept} value={dept}>{dept}</option>
                  ))}
                </select>
              </div>
              <div style={{ flex: 1 }}>
                <input
                  type="text"
                  placeholder="Search agents..."
                  value={agentSearch}
                  onChange={(e) => setAgentSearch(e.target.value)}
                  style={{ 
                    width: '100%',
                    padding: '0.5rem',
                    borderRadius: '4px',
                    border: '1px solid #d9d9d9'
                  }}
                />
              </div>
            </div>
            
            {/* Agent Cards */}
            {sortedAgents.length > 0 ? (
              <div style={{ 
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
                gap: '1rem'
              }}>
                {sortedAgents.map((agent) => (
                  <div key={agent.id || agent.email} style={{ 
                    backgroundColor: '#fff',
                    borderRadius: '8px',
                    padding: '1rem',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                    border: '1px solid #f0f0f0'
                  }}>
                    <div style={{ marginBottom: '0.5rem' }}>
                      <h5 style={{ margin: '0 0 0.25rem 0', fontSize: '1rem' }}>
                        {agent.name || 'Unnamed Agent'}
                      </h5>
                      <p style={{ margin: '0', color: '#666', fontSize: '0.85rem' }}>
                        {agent.email}
                      </p>
                    </div>
                    
                    <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
                      {agent.department && (
                        <span style={{ 
                          padding: '0.15rem 0.4rem', 
                          borderRadius: '4px',
                          fontSize: '0.75rem',
                          backgroundColor: '#e6f7ff',
                          color: '#1890ff'
                        }}>
                          {agent.department}
                        </span>
                      )}
                      <span style={{ 
                        padding: '0.15rem 0.4rem', 
                        borderRadius: '4px',
                        fontSize: '0.75rem',
                        backgroundColor: agent.status === 'active' ? '#f6ffed' : '#fff7e6',
                        color: agent.status === 'active' ? '#52c41a' : '#fa8c16'
                      }}>
                        {agent.status.charAt(0).toUpperCase() + agent.status.slice(1)}
                      </span>
                    </div>
                    
                    {agent.description && (
                      <p style={{ 
                        margin: '0.5rem 0 0 0', 
                        fontSize: '0.85rem',
                        color: '#333',
                        lineHeight: '1.4'
                      }}>
                        {agent.description.length > 100 
                          ? agent.description.substring(0, 100) + '...' 
                          : agent.description}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '2rem', color: '#666', backgroundColor: '#f9f9f9', borderRadius: '8px' }}>
                <p>No AI agents match your filter criteria.</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default MembersTab;
